import { useEffect } from "react";
import classes from "./AnnouncementBanner.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsDesktop from "hooks/useIsDesktop";
import useWhitelabel from "hooks/useWhitelabel";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import Image from "@Components/Image";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { AMBER_USER_WEBSITE_CONFIGURABLE_COMPONENTS } from "constants/index";
import Text from "@Icons/amber_sale/Text";
import MarqueeText from "@Components/MarqueeText";
import config from "@Config/index";
import { updateIsSaleBannerActiveOnHomePage } from "@App/appReducer";

const AnnouncementBanner = () => {
  const { t } = useTranslationHook();
  const isDesktop = useIsDesktop();
  const { isWhitelabel } = useWhitelabel();
  const { isBelow } = useResponsiveness();
  const isBelowSM = isBelow(SCREEN_SIZE.SM);
  const dispatch = useDispatch();
  const { configComponents } = useSelector(
    ({ appData }: AppState) => ({
      showAppBanner: appData.showAppBanner,
      configComponents: appData.configComponents,
    }),
    shallowEqual,
  );
  const { propertyViewedFlag, searchViewedFlag } = useSelector(
    ({ appData }: AppState) => ({
      propertyViewedFlag: appData.propertyViewedFlag,
      searchViewedFlag: appData.searchViewedFlag,
    }),
    shallowEqual,
  );
  const isRepeatUser = propertyViewedFlag || searchViewedFlag;
  const hasContinueWhereYouLeftOff = propertyViewedFlag;
  const needsBottomPadding = isDesktop && isRepeatUser && !hasContinueWhereYouLeftOff;
  const bannerConfigComponent = configComponents?.filter(
    (configComponent: any) =>
      configComponent?.component_name ==
      AMBER_USER_WEBSITE_CONFIGURABLE_COMPONENTS["home_page_top_banner"],
  )?.[0];

  useEffect(() => {
    dispatch(updateIsSaleBannerActiveOnHomePage(bannerConfigComponent?.data?.title?.text));
  }, [bannerConfigComponent?.data?.title?.text, dispatch]);

  const images = bannerConfigComponent?.data?.image?.split(",");
  const leftImg = images?.[0];
  const rightImg = images?.[1];

  const SalesOfferBannerMobile = () =>
    bannerConfigComponent?.data?.subtitle?.highlighted_text ? (
      <div
        className={formatClassNames(
          classes.containerWrapper,
          needsBottomPadding && classes.withBottomPadding,
        )}
      >
        <div className={formatClassNames(classes.salesBanner, isDesktop && classes.desktop)}>
          {leftImg && (
            <div className={classes.salesCoin}>
              <Image src={leftImg} height={24} width={24} />
            </div>
          )}
          <div className={classes.marqueeContainer}>
            <MarqueeText
              text={t(bannerConfigComponent?.data?.subtitle?.highlighted_text) || ""}
              startPosition="middle"
              width="100%"
              className={classes.subText}
            />
          </div>
          {rightImg && (
            <div className={classes.salesCoin}>
              <Image src={rightImg} height={24} width={24} />
            </div>
          )}
        </div>
      </div>
    ) : (
      <></>
    );

  const SalesOfferBannerDesktop = () =>
    bannerConfigComponent?.data?.title?.highlighted_text ? (
      <div
        className={formatClassNames(
          classes.containerWrapper,
          needsBottomPadding && classes.withBottomPadding,
        )}
      >
        <div className={formatClassNames(classes.salesBanner, isDesktop && classes.desktop)}>
          {leftImg && (
            <div className={classes.salesCoin}>
              <Image src={leftImg} height={24} width={24} />
            </div>
          )}
          <span className={classes.boldText}>
            <Text isChina={config.IS_CHINA_APP} />
          </span>
          {bannerConfigComponent?.data?.title?.highlighted_text && (
            <MarqueeText
              text={t(bannerConfigComponent?.data?.title?.highlighted_text) || ""}
              startPosition="middle"
              width="100%"
              className={classes.subText}
            />
          )}
          {rightImg && (
            <div className={classes.salesCoin}>
              <Image src={rightImg} height={24} width={24} />
            </div>
          )}
        </div>
      </div>
    ) : (
      <></>
    );

  const renderSalesBanner = () => {
    const heroConfigComponent = configComponents?.filter(
      (configComponent: any) =>
        configComponent?.component_name ==
        AMBER_USER_WEBSITE_CONFIGURABLE_COMPONENTS["home_page_hero_section"],
    )?.[0];

    if (heroConfigComponent?.data?.image?.endsWith(".json") && !isDesktop) return <></>;
    return (
      <>{isDesktop && !isBelowSM ? <SalesOfferBannerDesktop /> : <SalesOfferBannerMobile />}</>
    );
  };

  return bannerConfigComponent?.data?.title?.text !== "amber-fest" &&
    bannerConfigComponent?.data?.title?.text ? (
    <span
      // href={bannerConfigComponent?.data?.href}
      className={formatClassNames(classes.container, isDesktop && classes.desktop)}
    >
      <div className={classes.leftRadial} />
      <div className={classes.rightRadial} />
      <div className={formatClassNames(classes.contentWrapper, isDesktop && classes.desktop)}>
        <Image
          src={
            bannerConfigComponent?.data?.image ||
            "https://prod-static-assets.amberstudent.com/images/time-running-out.gif?w=40"
          }
          height={26}
          width={26}
        />
        <div className={formatClassNames(classes.content, isDesktop && classes.desktop)}>
          <div
            className={formatClassNames(
              isWhitelabel ? "wl-title-font" : "",
              isDesktop && classes.smallText,
            )}
          >
            {t(bannerConfigComponent?.data?.title?.text)}
          </div>
          <div className={formatClassNames(classes.subContent, isDesktop && classes.smallText)}>
            {t(bannerConfigComponent?.data?.title?.highlighted_text)}
          </div>
        </div>
        {!isDesktop && (
          <div
            className={formatClassNames(
              classes.content,
              isDesktop && classes.desktop,
              classes.extraContent,
            )}
          >
            <Image
              src={
                bannerConfigComponent?.data?.image ||
                "https://prod-static-assets.amberstudent.com/images/time-running-out.gif?w=40"
              }
              height={26}
              width={26}
              style={{
                marginRight: "6px",
              }}
            />
            <div className={formatClassNames(isWhitelabel ? "wl-title-font" : "")}>
              {t(bannerConfigComponent?.data?.title?.text)}
            </div>
            <div className={classes.subContent}>
              {t(bannerConfigComponent?.data?.title?.highlighted_text)}
            </div>
          </div>
        )}
      </div>
    </span>
  ) : (
    renderSalesBanner()
  );
};

export default wrapperHOC(AnnouncementBanner, {
  componentName: "AnnouncementBanner-Global",
  showForChina: true,
});
